// {{mergeField}} substitution for the templates picker. Values are pulled from
// the recipient's matched CRM Person/Company (plus the sender), so an investor
// outreach template like "CLS Wind — lifting technology for {{company}}" fills
// in automatically when the recipient is a known contact.

export type MergeContext = Record<string, string>;

// Build the merge map from whatever records resolved. Keys are case-insensitive
// at substitution time. Empty values are kept out so an unmatched field is left
// visibly as {{field}} rather than blanked (the user can see what to fill).
export const buildMergeContext = (parts: {
  firstName?: string;
  lastName?: string;
  company?: string;
  jobTitle?: string;
  senderName?: string;
  senderEmail?: string;
}): MergeContext => {
  const fullName = `${parts.firstName ?? ''} ${parts.lastName ?? ''}`.trim();
  const entries: [string, string | undefined][] = [
    ['firstName', parts.firstName],
    ['lastName', parts.lastName],
    ['fullName', fullName],
    ['name', fullName],
    ['company', parts.company],
    ['jobTitle', parts.jobTitle],
    ['senderName', parts.senderName],
    ['senderEmail', parts.senderEmail],
  ];
  const context: MergeContext = {};
  for (const [key, value] of entries) {
    if (value) context[key.toLowerCase()] = value;
  }
  return context;
};

const FIELD_RE = /\{\{\s*([\w.]+)\s*\}\}/g;

// Replace {{field}} tokens from the context. Unknown/empty fields are left
// untouched so the user notices and edits them. Returns the filled text and the
// list of fields that had no value (for an in-pane hint).
export const applyMerge = (
  text: string,
  context: MergeContext,
): { text: string; missing: string[] } => {
  const missing = new Set<string>();
  const filled = text.replace(FIELD_RE, (match, field: string) => {
    const value = context[field.toLowerCase()];
    if (value) return value;
    missing.add(field);
    return match;
  });
  return { text: filled, missing: Array.from(missing) };
};
